/**
 * Transcript Utilities
 *
 * Utilities for formatting, parsing, and managing conversation transcripts.
 */

export interface TranscriptMessage {
  speaker: 'user' | 'assistant'
  content: string
  timestamp?: Date
  metadata?: Record<string, any>
}

export interface ParsedTranscript {
  messages: TranscriptMessage[]
  metadata?: {
    duration?: number
    totalTokens?: number
    model?: string
    conversationType: 'voice' | 'text'
  }
}

/**
 * Formats a transcript message for storage
 */
export function formatTranscriptMessage(
  speaker: 'user' | 'assistant',
  content: string,
  timestamp?: Date
): string {
  const timestampStr = timestamp ? ` [${timestamp.toISOString()}]` : ''
  return `${speaker === 'user' ? 'User' : 'Assistant'}: ${content}${timestampStr}`
}

/**
 * Normalizes transcript: if it's a JSON array of {role, content}, convert to User/Assistant lines
 */
function normalizeTranscript(transcript: string): string {
  const trimmed = transcript.trim()
  if (trimmed.startsWith("[") && trimmed.includes('"role"')) {
    try {
      const arr = JSON.parse(trimmed) as Array<{ role?: string; content?: string }>
      if (Array.isArray(arr)) {
        return arr
          .map((m) => {
            const speaker = m.role === "user" ? "User" : "Assistant"
            const content = typeof m.content === "string" ? m.content : ""
            return `${speaker}: ${content}`
          })
          .join("\n\n")
      }
    } catch {
      // Fall through to normal parse
    }
  }
  return transcript
}

/**
 * Strips Nova Sonic / voice control tokens that sometimes leak into the raw
 * text output (e.g. `{ "interrupted" : true }`) and collapses the whitespace
 * they leave behind. Safe for display and for persistence.
 */
export function sanitizeTranscriptContent(content: string): string {
  if (!content) return ''
  return content
    // Remove standalone control-token JSON blobs like { "interrupted" : true }
    .replace(/\{\s*"(?:interrupted|stopReason|generationStage|barge_?in)"\s*:\s*[^}]*\}/gi, '')
    // Remove any leftover bare {interrupted:true}-style fragments
    .replace(/\{\s*interrupted\s*:\s*(?:true|false)\s*\}/gi, '')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/**
 * Parses a raw transcript string into structured messages
 */
export function parseTranscript(transcript: string): ParsedTranscript {
  const normalized = normalizeTranscript(transcript)
  // Split inline speaker turns: voice transcripts sometimes concatenate the
  // agent reply into the customer line ("... my name is ayo\nAgent: Thanks").
  // Insert line breaks before any inline Agent:/Assistant:/User:/Customer:
  // prefix so each turn parses as its own message.
  const withTurnBreaks = normalized
    .replace(/\s+(Agent|Assistant):\s/g, '\nAssistant: ')
    .replace(/\s+(Customer|User|Caller):\s/g, '\nUser: ')
  const lines = withTurnBreaks.split("\n").filter((line) => line.trim())
  const messages: TranscriptMessage[] = []

  const pushMsg = (speaker: 'user' | 'assistant', raw: string, timestamp?: Date) => {
    const content = sanitizeTranscriptContent(raw)
    if (!content) return
    messages.push({ speaker, content, timestamp })
  }

  for (const line of lines) {
    if (/^(User|Customer|Caller): /.test(line)) {
      const content = line.replace(/^(User|Customer|Caller): /, '').trim()
      const timestampMatch = content.match(/ \[(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z)\]$/)
      const cleanContent = timestampMatch ? content.replace(timestampMatch[0], '') : content
      const timestamp = timestampMatch ? new Date(timestampMatch[1]) : undefined
      pushMsg('user', cleanContent, timestamp)
    } else if (/^(Assistant|Agent): /.test(line)) {
      const content = line.replace(/^(Assistant|Agent): /, '').trim()
      const timestampMatch = content.match(/ \[(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z)\]$/)
      const cleanContent = timestampMatch ? content.replace(timestampMatch[0], '') : content
      const timestamp = timestampMatch ? new Date(timestampMatch[1]) : undefined
      pushMsg('assistant', cleanContent, timestamp)
    } else if (line.trim()) {
      // Continuation of previous message
      if (messages.length > 0) {
        const merged = sanitizeTranscriptContent(
          messages[messages.length - 1].content + '\n' + line.trim()
        )
        messages[messages.length - 1].content = merged
      }
    }
  }

  return { messages }
}

/**
 * Accumulates a new message into an existing transcript
 */
export function accumulateTranscript(
  existingTranscript: string | undefined,
  newMessage: TranscriptMessage
): string {
  const formattedMessage = formatTranscriptMessage(
    newMessage.speaker,
    newMessage.content,
    newMessage.timestamp
  )

  if (!existingTranscript || existingTranscript.trim().length === 0) {
    return formattedMessage
  }

  return `${existingTranscript}\n\n${formattedMessage}`
}

/**
 * Creates a complete transcript from message history
 */
export function createTranscriptFromMessages(messages: TranscriptMessage[]): string {
  return messages
    .map(msg => formatTranscriptMessage(msg.speaker, msg.content, msg.timestamp))
    .join('\n\n')
}

/**
 * Validates transcript format
 */
export function validateTranscript(transcript: string): boolean {
  if (!transcript || transcript.trim().length === 0) {
    return false
  }

  const lines = transcript.split('\n').filter(line => line.trim())
  let hasValidMessages = false

  for (const line of lines) {
    if (line.startsWith('User: ') || line.startsWith('Assistant: ')) {
      hasValidMessages = true
      break
    }
  }

  return hasValidMessages
}

/**
 * Extracts conversation statistics from transcript
 */
export function getTranscriptStats(transcript: string): {
  messageCount: number
  userMessages: number
  assistantMessages: number
  totalLength: number
  estimatedTokens: number
} {
  const parsed = parseTranscript(transcript)

  return {
    messageCount: parsed.messages.length,
    userMessages: parsed.messages.filter(m => m.speaker === 'user').length,
    assistantMessages: parsed.messages.filter(m => m.speaker === 'assistant').length,
    totalLength: transcript.length,
    estimatedTokens: Math.ceil(transcript.length / 4) // Rough estimate: 1 token ≈ 4 characters
  }
}

/**
 * Truncates transcript to fit within token limits while preserving recent messages
 */
export function truncateTranscript(
  transcript: string,
  maxTokens: number = 2000,
  keepRecentMessages: number = 5
): string {
  const parsed = parseTranscript(transcript)
  const stats = getTranscriptStats(transcript)

  if (stats.estimatedTokens <= maxTokens) {
    return transcript
  }

  // Keep the most recent messages, plus some older context
  const recentMessages = parsed.messages.slice(-keepRecentMessages)
  const contextMessages = parsed.messages.slice(0, Math.max(0, parsed.messages.length - keepRecentMessages))

  // Try to fit as many context messages as possible
  let selectedMessages = [...recentMessages]
  let currentTokens = getTranscriptStats(createTranscriptFromMessages(recentMessages)).estimatedTokens

  // Add context messages from newest to oldest until we hit the limit
  for (let i = contextMessages.length - 1; i >= 0 && currentTokens < maxTokens; i--) {
    const testMessages = [...contextMessages.slice(i), ...recentMessages]
    const testTranscript = createTranscriptFromMessages(testMessages)
    const testTokens = getTranscriptStats(testTranscript).estimatedTokens

    if (testTokens <= maxTokens) {
      selectedMessages = testMessages
      currentTokens = testTokens
    } else {
      break
    }
  }

  return createTranscriptFromMessages(selectedMessages)
}
